import type { Lang } from './lang.js';

/** Fils (AED minor units, bigint-safe integers) → display string. Doc 12 §2. */
export function formatFils(amountFils: number, lang: Lang): string {
  const aed = amountFils / 100;
  const formatted = new Intl.NumberFormat(lang === 'ar' ? 'ar-AE' : 'en-AE', {
    minimumFractionDigits: aed % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
    // Western Arabic digits everywhere for amounts (doc 10 §3)
    numberingSystem: 'latn',
  }).format(aed);
  return lang === 'ar' ? `${formatted} درهم` : `AED ${formatted}`;
}

/** Wrap Latin tokens (references, URLs, plates) in bidi isolates so they never
 *  scramble Arabic lines (doc 10 §4). */
export function bidiIsolate(token: string): string {
  return `\u2068${token}\u2069`; // FSI … PDI
}

/** The UAE Dirham sign, U+20C3, encoded in Unicode 18.0 (September 2026). */
export const DIRHAM_SIGN = '\u20C3';

/** An amount as `formatFils` writes one: Western digits, optional thousands
 *  separators, optional decimals. Eastern Arabic numerals are deliberately
 *  excluded — those only appear in hand-written prose that spells the amount
 *  out, which keeps the word (see `toDirhamSymbol`). */
const AMOUNT = String.raw`\d[\d,]*(?:\.\d+)?`;

const PREFIXED_EN = new RegExp(String.raw`\bAED\s*(${AMOUNT})`, 'g');

/* `(?!\s*=)` spares a unit definition like "1 AED = 100 fils", where the
 * currency is the thing being defined rather than a sum being quoted. The
 * central bank's guideline keeps the written word in that kind of prose. */
const SUFFIXED_EN = new RegExp(String.raw`(${AMOUNT})\s*AED\b(?!\s*=)`, 'g');

const SUFFIXED_AR = new RegExp(String.raw`(${AMOUNT})\s*درهم`, 'g');

const RTL_SCRIPT = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Presentation only: rewrite a quoted sum to lead with the dirham sign —
 * "AED 150", "150 AED" and "150 درهم" all become "⃃ 150", the order the central
 * bank's guideline specifies.
 *
 * This runs *after* the engine, never inside it. `formatFils` above feeds
 * `moneyFacts`, and therefore the grounding check, the guardrail claim
 * patterns, the PDF templates and the voice fallback — a glyph is silent in
 * text-to-speech, so a citizen would hear the number with no currency at all.
 * Keeping the substitution at the render seam leaves every one of those on the
 * canonical "AED 150" they already assert.
 *
 * Bidi isolates are added only when the surrounding text actually carries
 * Arabic script. An LTR-only amount needs no protection, and the isolates are
 * invisible characters that would otherwise show up in every string
 * comparison for no benefit.
 *
 * Idempotent: a second pass finds no "AED" or "درهم" left to match.
 */
export function toDirhamSymbol(text: string): string {
  if (!text) return text;
  const isolate = RTL_SCRIPT.test(text);
  const money = (amount: string): string => {
    const pair = `${DIRHAM_SIGN} ${amount}`;
    return isolate ? bidiIsolate(pair) : pair;
  };
  return text
    .replace(PREFIXED_EN, (_match, amount: string) => money(amount))
    .replace(SUFFIXED_EN, (_match, amount: string) => money(amount))
    .replace(SUFFIXED_AR, (_match, amount: string) => money(amount));
}

/**
 * A reply with its Markdown taken off, for the surfaces that have no renderer.
 *
 * Text-to-speech is the one that matters: `spokenForm` falls back to
 * `EngineReply.text`, and a synthesiser handed "**AED 150**" either says
 * "asterisk asterisk" or swallows the sum. Bullets are the same problem in the
 * other direction — a list read aloud as one breathless sentence is unusable,
 * so each item ends up a sentence of its own.
 *
 * Deliberately not a Markdown parser. It removes the markers the model was told
 * it may write and leaves everything else alone, because anything it fails to
 * recognise is spoken as written, which is the safe way to be wrong.
 */
export function stripMarkdown(text: string): string {
  if (!text) return text;
  const lines = text
    .split('\n')
    .map((line) => {
      if (/^\s*([-*_])(?:\s*\1){2,}\s*$/.test(line)) return '';
      return line
        .replace(/^ {0,3}#{1,6}\s+/, '')
        .replace(/^\s*>\s?/, '')
        // A spoken list needs a full stop between items or it runs together.
        .replace(/^(\s*)[-*+][ \t]+/, '$1')
        .replace(/^(\s*)\d+\.[ \t]+/, '$1')
        .trimEnd();
    })
    .filter((line) => line.trim().length > 0);

  return lines
    .map((line) => (/[.!?؟۔:,؛]$/.test(line.trim()) ? line : `${line}.`))
    .join(' ')
    .replace(/\[([^\]\n]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/(^|[^\w*])\*([^*\n]+)\*(?![\w*])/g, '$1$2')
    .replace(/(^|[^\w_])_([^_\n]+)_(?![\w_])/g, '$1$2')
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function formatDate(iso: string | Date, lang: Lang): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-AE' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    numberingSystem: 'latn',
  }).format(date);
}

/** Fractional SLA days → human units without leaking implementation decimals.
 * Examples: 1/6 day → "4 hours", 1/24 day → "1 hour", 1.5 days →
 * "1 working day 12 hours". Values are rounded to a useful five/fifteen-minute
 * boundary because published turnaround times are estimates, not timers. */
export function formatServiceDuration(decisionDays: number, lang: Lang): string {
  if (!Number.isFinite(decisionDays) || decisionDays <= 0) {
    return lang === 'ar' ? 'غير محدد' : 'Not specified';
  }

  const rawMinutes = decisionDays * 24 * 60;
  const roundedMinutes =
    rawMinutes < 60
      ? Math.max(1, Math.round(rawMinutes / 5) * 5)
      : Math.max(15, Math.round(rawMinutes / 15) * 15);
  const days = Math.floor(roundedMinutes / (24 * 60));
  const remainingMinutes = roundedMinutes - days * 24 * 60;
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes - hours * 60;

  if (lang === 'ar') {
    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ${days === 1 ? 'يوم عمل' : 'أيام عمل'}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`);
    if (minutes > 0 || parts.length === 0) {
      parts.push(`${minutes || roundedMinutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`);
    }
    return parts.join(' و');
  }

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} working ${days === 1 ? 'day' : 'days'}`);
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (minutes > 0 || parts.length === 0) {
    parts.push(`${minutes || roundedMinutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }
  return parts.join(' ');
}

/**
 * The forms a noun takes once it is counted.
 *
 * English needs two. Arabic needs four, and the one that gets dropped is
 * always the dual: `قاربان` is a distinct form, not `2 قارب`, and writing the
 * plural there is the kind of mistake a reader notices immediately on a
 * government page. `many` covers 11 and up, where the noun returns to the
 * accusative singular — `11 قارباً`, not `11 قوارب`.
 */
export interface CountForms {
  en: { one: string; other: string };
  ar: { one: string; two: string; few: string; many: string };
}

/**
 * A count and its noun, agreeing in both languages.
 *
 * Western digits throughout, matching `formatFils` and the rest of this file:
 * the ministry's own portals quote numbers that way and a citizen comparing
 * two screens should not have to read two numbering systems.
 */
export function countedNoun(count: number, forms: CountForms, lang: Lang): string {
  if (lang !== 'ar') {
    return `${count} ${count === 1 ? forms.en.one : forms.en.other}`;
  }
  // One and two name themselves in Arabic; repeating the digit reads as a
  // form filled in by a machine.
  if (count === 1) return forms.ar.one;
  if (count === 2) return forms.ar.two;
  // 3-10 take the plural, 11+ the accusative singular. The rule runs on the
  // last two digits, so 103 counts as few and 111 as many.
  const tail = count % 100;
  const few = tail >= 3 && tail <= 10;
  return `${count} ${few ? forms.ar.few : forms.ar.many}`;
}

/**
 * A money amount in every form a reply might carry it.
 *
 * The grounding check normalises whitespace and compares whole claim strings,
 * so a claim of "AED 100" becomes `aed100` and a fact of `100` can never match
 * it. Every fact set here pushed bare numbers, which meant no money claim could
 * ever be grounded — the check was not strict, it was broken, and it flagged
 * correct copy on every turn that mentioned a fee.
 *
 * Both languages, because a reply is grounded against the same set whichever
 * one the citizen is reading.
 */
export function moneyFacts(amountFils: number): string[] {
  return [
    String(amountFils),
    String(amountFils / 100),
    (amountFils / 100).toFixed(2),
    formatFils(amountFils, 'en'),
    formatFils(amountFils, 'ar'),
  ];
}

/** After the start of the token, and after each mark a name is built across. */
const NAME_BOUNDARY = /(^|[-'’.])(\p{L})/gu;

/**
 * A name as a person would write it, from one that arrived shouting.
 *
 * UAE Pass returns `fullnameEN` in block capitals, so a portal that greeted
 * somebody with what it was given said "Welcome back, DMITRY" — which is not a
 * greeting, it is a summons.
 *
 * **Presentation only, and never the record.** What the identity provider sent
 * is what the Ministry holds, what the held-identity panel mirrors back under
 * an invitation to correct it at UAE Pass, and what goes into a filing body, a
 * Qarar and a bank letter. Casing is a choice about type; the name is not.
 * Call this at the seam where a name is drawn, not where one is stored.
 *
 * Only a token with no lowercase in it at all is touched. A name that already
 * carries case — `McDonald`, `van der Berg`, anything a citizen typed for
 * themselves — is left exactly as it is, because having been shouted is the
 * one thing that distinguishes a name needing help from a name that is already
 * right. Arabic has no case, so every Arabic token fails the same test and
 * passes through untouched; this needs no script check of its own.
 *
 * `MCDONALD` does become `Mcdonald`. Nothing can tell it from `MACLEOD`, and
 * one wrong lowercase letter reads better than the whole name in capitals.
 */
export function humanizeName(name: string): string {
  return name.replace(/\S+/gu, (token) => {
    const lower = token.toLocaleLowerCase('en');
    if (token !== token.toLocaleUpperCase('en') || token === lower) return token;
    return lower.replace(NAME_BOUNDARY, (_, mark: string, letter: string) =>
      mark + letter.toLocaleUpperCase('en'),
    );
  });
}
